import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Activity, CheckCircle, AlertTriangle, ArrowLeft, ArrowRight, Send, Plus, X, GripVertical } from 'lucide-react';

export default function SurveyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [surveyData, setSurveyData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [skippedQuestions, setSkippedQuestions] = useState({}); // { questionId: skipReason }
  const [showSkipOptions, setShowSkipOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const skipReasons = [
    { value: 'dont_know', label: "I don't know" },
    { value: 'no_opinion', label: "I don't have an opinion" },
    { value: 'afraid_to_say', label: "I'm afraid to say" },
    { value: 'other', label: "Other" },
  ];

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const response = await surveyAPI.get(token);
        setSurveyData(response.data);
        // Initialize responses
        const initialResponses = {};
        response.data.questions.forEach(q => {
          if (q.question_type === 'checkbox') {
            initialResponses[q.id] = [];
          } else if (q.question_type === 'slider' || q.question_type === 'rating') {
            initialResponses[q.id] = 5;
          } else if (q.question_type === 'multi_topic') {
            initialResponses[q.id] = []; // Array of topic strings
          } else {
            initialResponses[q.id] = '';
          }
        });
        setResponses(initialResponses);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [token]);

  const currentQuestion = surveyData?.questions?.[currentIndex];
  const progress = surveyData?.questions ? ((currentIndex + 1) / surveyData.questions.length) * 100 : 0;

  const updateResponse = (value) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const toggleCheckbox = (option) => {
    const current = responses[currentQuestion.id] || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    updateResponse(updated);
  };

  // Multi-topic functions
  const [newTopicInput, setNewTopicInput] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const touchState = React.useRef({ startY: 0, currentIndex: null, isDragging: false, startX: 0 });

  const addTopic = () => {
    if (!newTopicInput.trim()) return;
    const current = responses[currentQuestion.id] || [];
    updateResponse([...current, newTopicInput.trim()]);
    setNewTopicInput('');
  };

  const removeTopic = (index) => {
    const current = responses[currentQuestion.id] || [];
    updateResponse(current.filter((_, i) => i !== index));
  };

  const handleTopicDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleTopicDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const current = [...(responses[currentQuestion.id] || [])];
    const draggedItem = current[draggedIndex];
    current.splice(draggedIndex, 1);
    current.splice(index, 0, draggedItem);
    updateResponse(current);
    setDraggedIndex(index);
  };

  const handleTopicDragEnd = () => {
    setDraggedIndex(null);
  };

  // Touch-based drag and drop for mobile
  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    touchState.current = {
      startY: touch.clientY,
      currentIndex: index,
      isDragging: false,
      startX: touch.clientX,
    };
    setDraggedIndex(index);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - touchState.current.startY);
    const deltaX = Math.abs(touch.clientX - touchState.current.startX);
    
    // If horizontal movement is greater, it's likely a scroll attempt - allow it
    if (deltaX > deltaY && !touchState.current.isDragging) return;
    
    // Start dragging after 10px vertical movement
    if (deltaY > 10) {
      touchState.current.isDragging = true;
      e.preventDefault(); // Prevent scrolling only when dragging
    }
    
    if (!touchState.current.isDragging) return;
    
    // Find which item we're over
    const container = e.currentTarget;
    if (!container) return;
    
    const items = container.querySelectorAll('[data-topic-item]');
    const touchY = touch.clientY;
    
    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      
      if (touchY < midY && i !== touchState.current.currentIndex) {
        // Reorder items
        const current = [...(responses[currentQuestion.id] || [])];
        const draggedItem = current[touchState.current.currentIndex];
        current.splice(touchState.current.currentIndex, 1);
        current.splice(i, 0, draggedItem);
        updateResponse(current);
        touchState.current.currentIndex = i;
        setDraggedIndex(i);
        break;
      } else if (touchY > midY && touchY < rect.bottom && i !== touchState.current.currentIndex && i === items.length - 1) {
        // Handle dropping at the end
        const current = [...(responses[currentQuestion.id] || [])];
        const draggedItem = current[touchState.current.currentIndex];
        current.splice(touchState.current.currentIndex, 1);
        current.push(draggedItem);
        updateResponse(current);
        touchState.current.currentIndex = current.length - 1;
        setDraggedIndex(current.length - 1);
        break;
      }
    }
  };

  const handleTouchEnd = () => {
    touchState.current = { startY: 0, currentIndex: null, isDragging: false, startX: 0 };
    setDraggedIndex(null);
  };

  const canProceed = () => {
    if (!currentQuestion) return false;
    
    // If question was skipped, can proceed
    if (skippedQuestions[currentQuestion.id]) return true;
    
    if (!currentQuestion.required) return true;
    
    const answer = responses[currentQuestion.id];
    if (currentQuestion.question_type === 'checkbox' || currentQuestion.question_type === 'multi_topic') {
      return answer && answer.length > 0;
    }
    if (currentQuestion.question_type === 'slider' || currentQuestion.question_type === 'rating') {
      return true; // Always has a default value
    }
    return answer && answer.toString().trim() !== '';
  };

  const handleSkip = (reason) => {
    setSkippedQuestions(prev => ({
      ...prev,
      [currentQuestion.id]: reason
    }));
    setShowSkipOptions(false);
    // Clear any answer for this question
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: null
    }));
    // Move to next question
    if (currentIndex < surveyData.questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleUnskip = () => {
    setSkippedQuestions(prev => {
      const updated = { ...prev };
      delete updated[currentQuestion.id];
      return updated;
    });
  };

  const handleNext = () => {
    if (currentIndex < surveyData.questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowSkipOptions(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowSkipOptions(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedResponses = Object.entries(responses)
        .filter(([questionId, answer]) => !skippedQuestions[questionId] && answer !== null)
        .map(([questionId, answer]) => ({
          question_id: questionId,
          answer: answer
        }));
      
      // Add skipped questions with their reasons
      Object.entries(skippedQuestions).forEach(([questionId, reason]) => {
        formattedResponses.push({
          question_id: questionId,
          answer: null,
          skipped: true,
          skip_reason: reason
        });
      });
      
      await surveyAPI.submit(token, formattedResponses);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const getDriverName = (driverId) => {
    return surveyData?.drivers?.find(d => d.id === driverId)?.name || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-2xl w-full">
          <Skeleton className="h-96" />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="py-16 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {error === 'Invitation expired' ? 'Invitation Expired' : 'Invalid Link'}
            </h2>
            <p className="text-slate-500">
              {error === 'Invitation expired' 
                ? 'This survey invitation has expired. Please contact the administrator for a new link.'
                : 'The survey link is invalid. Please check your email for the correct link.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Thank You!
            </h2>
            <p className="text-slate-600">
              Your responses have been recorded. Your feedback helps us understand 
              stakeholder sentiment and improve our project outcomes.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img src="/pulse-logo-with-name.png" alt="Pulse" className="h-10 object-contain" />
          <div>
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {surveyData?.project?.name}
            </h1>
            <p className="text-sm text-slate-500">
              Welcome, {surveyData?.stakeholder?.first_name}
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
            <span>Question {currentIndex + 1} of {surveyData?.questions?.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="shadow-lg mb-6">
          <CardHeader className="pb-2">
            {getDriverName(currentQuestion?.driver_id) && (
              <p className="text-xs font-medium text-[#5AA2AE] uppercase tracking-wide mb-1">
                {getDriverName(currentQuestion?.driver_id)}
              </p>
            )}
            {currentQuestion?.heading && (
              <p className="text-sm font-semibold text-slate-700 mb-2">
                {currentQuestion.heading}
              </p>
            )}
            <CardTitle className="text-lg leading-relaxed">
              {currentQuestion?.text}
              {currentQuestion?.required && <span className="text-red-500 ml-1">*</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Free Text */}
            {currentQuestion?.question_type === 'free_text' && (
              <Textarea
                placeholder="Type your response here..."
                value={responses[currentQuestion.id] || ''}
                onChange={(e) => updateResponse(e.target.value)}
                rows={4}
                data-testid="survey-freetext-input"
              />
            )}

            {/* Multiple Choice */}
            {currentQuestion?.question_type === 'multiple_choice' && (
              <RadioGroup 
                value={responses[currentQuestion.id] || ''} 
                onValueChange={updateResponse}
                className="space-y-3"
              >
                {currentQuestion.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="font-normal cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {/* Dropdown */}
            {currentQuestion?.question_type === 'dropdown' && (
              <Select 
                value={responses[currentQuestion.id] || ''} 
                onValueChange={updateResponse}
              >
                <SelectTrigger data-testid="survey-dropdown-select">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {currentQuestion.options?.map((option, index) => (
                    <SelectItem key={index} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Slider */}
            {currentQuestion?.question_type === 'slider' && (() => {
              const config = currentQuestion.slider_config || {};
              return (
                <div className="space-y-6 py-4">
                  <Slider
                    value={[responses[currentQuestion.id] || 5]}
                    onValueChange={([value]) => updateResponse(value)}
                    min={config.min_value ?? 0}
                    max={config.max_value ?? 10}
                    step={1}
                    className="w-full"
                    data-testid="survey-slider"
                  />
                  <div className="flex justify-between text-sm">
                    <span style={config.left_color ? { color: config.left_color } : { color: '#64748b' }}>
                      {config.left_label || 'Low'}
                    </span>
                    <span className="text-lg font-bold" style={config.middle_color ? { color: config.middle_color } : { color: '#5AA2AE' }}>
                      {config.middle_label || ''}
                    </span>
                    <span style={config.right_color ? { color: config.right_color } : { color: '#64748b' }}>
                      {config.right_label || 'High'}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Yes/No */}
            {currentQuestion?.question_type === 'yes_no' && (
              <RadioGroup 
                value={responses[currentQuestion.id] || ''} 
                onValueChange={updateResponse}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="yes" />
                  <Label htmlFor="yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="no" />
                  <Label htmlFor="no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
            )}

            {/* Checkbox (Multiple) */}
            {currentQuestion?.question_type === 'checkbox' && (
              <div className="space-y-3">
                {currentQuestion.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Checkbox
                      id={`check-${index}`}
                      checked={(responses[currentQuestion.id] || []).includes(option)}
                      onCheckedChange={() => toggleCheckbox(option)}
                    />
                    <Label htmlFor={`check-${index}`} className="font-normal cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </div>
            )}

            {/* Multi-Topic (Ranked List) */}
            {currentQuestion?.question_type === 'multi_topic' && (
              <div className="space-y-4">
                {/* List of topics */}
                <div 
                  className="space-y-2"
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {(responses[currentQuestion.id] || []).map((topic, index) => (
                    <div
                      key={index}
                      data-topic-item
                      draggable
                      onDragStart={() => handleTopicDragStart(index)}
                      onDragOver={(e) => handleTopicDragOver(e, index)}
                      onDragEnd={handleTopicDragEnd}
                      onTouchStart={(e) => handleTouchStart(e, index)}
                      className={`flex items-center gap-2 p-3 bg-white border rounded-lg shadow-sm transition-all touch-none ${
                        draggedIndex === index ? 'opacity-50 border-[#5AA2AE] scale-105' : 'border-slate-200'
                      }`}
                    >
                      <GripVertical className="w-4 h-4 text-slate-400 cursor-grab flex-shrink-0" />
                      <span className="w-6 h-6 rounded-full bg-[#5AA2AE] text-white text-xs flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-slate-700">{topic}</span>
                      <button
                        type="button"
                        onClick={() => removeTopic(index)}
                        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new topic input */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter a topic and press Add..."
                    value={newTopicInput}
                    onChange={(e) => setNewTopicInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTopic();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addTopic}
                    variant="outline"
                    className="flex-shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                {(responses[currentQuestion.id] || []).length > 1 && (
                  <p className="text-xs text-slate-500">
                    Drag items to reorder by priority (1 = highest priority)
                  </p>
                )}
              </div>
            )}

            {/* Skip Question UI */}
            {currentQuestion?.allow_skip && !skippedQuestions[currentQuestion.id] && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                {!showSkipOptions ? (
                  <button
                    type="button"
                    onClick={() => setShowSkipOptions(true)}
                    className="text-sm text-slate-400 hover:text-slate-600 underline"
                  >
                    Skip?
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600 font-medium">Why would you like to skip this question?</p>
                    <div className="flex flex-wrap gap-2">
                      {skipReasons.map(reason => (
                        <button
                          key={reason.value}
                          type="button"
                          onClick={() => handleSkip(reason.value)}
                          className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full transition-colors"
                        >
                          {reason.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSkipOptions(false)}
                      className="text-xs text-slate-400 hover:text-slate-600"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Skipped indicator */}
            {skippedQuestions[currentQuestion?.id] && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between bg-amber-50 text-amber-700 px-4 py-2 rounded-lg">
                  <span className="text-sm">
                    Skipped: {skipReasons.find(r => r.value === skippedQuestions[currentQuestion.id])?.label}
                  </span>
                  <button
                    type="button"
                    onClick={handleUnskip}
                    className="text-sm underline hover:text-amber-800"
                  >
                    Answer instead
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            data-testid="survey-prev-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          {currentIndex === surveyData.questions.length - 1 ? (
            <Button
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              data-testid="survey-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
              <Send className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="survey-next-btn"
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
